// Iter-46an: surfaces the closest 5 studios to a user who grants
// geolocation permission. Falls back gracefully:
// - permission denied → tiny "enable location for nearby studios" prompt
// - browser doesn't support geolocation → not rendered
// - backend hasn't geocoded studios yet → "Finding nearby studios…"
//   (returns immediately on subsequent visits)

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Navigation, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { formatStudioAddress } from '../lib/studioAddress';

const STORAGE_KEY = 'nearby_studios_permission';

export const NearbyStudiosWidget = ({ limit = 5, radiusKm = 50 }) => {
  const [permission, setPermission] = useState(() => sessionStorage.getItem(STORAGE_KEY) || 'idle');
  const [loading, setLoading] = useState(false);
  const [studios, setStudios] = useState([]);
  const [error, setError] = useState(null);

  // Auto-fetch if the user previously granted permission in this session.
  useEffect(() => {
    if (permission === 'granted') {
      void requestLocation(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requestLocation = async (silent = false) => {
    if (!('geolocation' in navigator)) {
      setError('Your browser does not support geolocation');
      return;
    }
    setLoading(true);
    setError(null);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        sessionStorage.setItem(STORAGE_KEY, 'granted');
        setPermission('granted');
        try {
          const url = `${process.env.REACT_APP_BACKEND_URL}/api/studios/nearby?lat=${pos.coords.latitude}&lng=${pos.coords.longitude}&limit=${limit}&radius_km=${radiusKm}`;
          const res = await fetch(url);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          setStudios(await res.json());
        } catch (e) {
          setError('Could not load nearby studios');
        } finally {
          setLoading(false);
        }
      },
      (geoErr) => {
        setLoading(false);
        sessionStorage.setItem(STORAGE_KEY, 'denied');
        setPermission('denied');
        if (!silent) {
          setError(
            geoErr.code === 1
              ? 'Location permission denied. Enable it in your browser to see nearby studios.'
              : 'Could not detect your location',
          );
        }
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 5 * 60 * 1000 },
    );
  };

  // Don't render anything if the browser has no geolocation API.
  if (typeof navigator !== 'undefined' && !('geolocation' in navigator)) {
    return null;
  }

  return (
    <Card className="border-primary/30" data-testid="nearby-studios-widget">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Navigation className="w-4 h-4 text-primary" />
          Studios near you
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {permission === 'idle' && (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm text-muted-foreground">
              Allow location access to see studios closest to you.
            </p>
            <Button
              size="sm"
              onClick={() => requestLocation(false)}
              data-testid="nearby-enable-btn"
            >
              <Navigation className="w-3 h-3 mr-1" />
              Use my location
            </Button>
          </div>
        )}

        {permission === 'denied' && !studios.length && (
          <p className="text-xs text-muted-foreground">
            {error || 'Location access blocked. Update your browser permissions to enable this.'}
          </p>
        )}

        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="nearby-loading">
            <Loader2 className="w-4 h-4 animate-spin" />
            Finding nearby studios…
          </div>
        )}

        {!loading && error && permission === 'granted' && (
          <p className="text-xs text-destructive">{error}</p>
        )}

        {!loading && studios.length > 0 && (
          <div className="space-y-2" data-testid="nearby-studios-list">
            {studios.map((studio, idx) => (
              <Link
                key={studio.id}
                to={`/studio/${studio.slug || studio.id}`}
                className="flex items-center justify-between gap-3 p-2 -mx-2 rounded-md hover:bg-muted transition-colors"
                data-testid={`nearby-studio-${idx}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{studio.name}</div>
                  <div className="text-xs text-muted-foreground truncate flex items-center gap-1">
                    <MapPin className="w-3 h-3 flex-shrink-0" />
                    {formatStudioAddress(studio) || 'Address unavailable'}
                  </div>
                </div>
                <Badge variant="secondary" className="flex-shrink-0">
                  {studio.distance_km != null
                    ? studio.distance_km < 1
                      ? `${Math.round(studio.distance_km * 1000)} m`
                      : `${studio.distance_km.toFixed(1)} km`
                    : '—'}
                </Badge>
              </Link>
            ))}
          </div>
        )}

        {!loading && permission === 'granted' && studios.length === 0 && !error && (
          <p className="text-xs text-muted-foreground">
            No studios within {radiusKm} km of your location yet.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default NearbyStudiosWidget;
